import { Component, type ErrorInfo, type ReactNode } from 'react';

import { ServerErrorPage } from '@/components/common/ServerErrorPage';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Centralized hook for shipping render errors to an observability
    // backend (e.g. Sentry). Kept as a single call site so wiring a real
    // reporter later doesn't require touching component code.
    console.error('Unhandled render error', error, errorInfo);
  }

  private handleReset = (): void => {
    this.setState({ hasError: false });
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      return <ServerErrorPage onRetry={this.handleReset} />;
    }

    return this.props.children;
  }
}
